/**
 * cesium_utils.js – Cesium / viewer utility functions.
 *
 * No React dependency – safe to import anywhere that has Cesium available
 * on the global `window` / `Cesium` namespace.
 */

// ---------------------------------------------------------------------------
// Polling helpers
// ---------------------------------------------------------------------------

/** Resolve after `ms` milliseconds. */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll until the Cesium viewer is fully initialised.
 * @param {number} maxAttempts - Total retry count (default 50 × 100 ms = 5 s)
 * @param {number} interval    - Check interval in ms (default 100)
 * @returns {Promise<Cesium.Viewer>}
 */
export const waitForViewerReady = async (maxAttempts = 50, interval = 100) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
            if (window.viewer && !window.viewer.isDestroyed() && window.viewer.camera?.positionWC) {
                console.log(`✅ Viewer ready after ${attempt * interval}ms`);
                return window.viewer;
            }
        } catch {
            // viewer getter threw – not ready yet
        }
        await sleep(interval);
    }
    throw new Error(`❌ Viewer failed to initialize after ${maxAttempts * interval}ms`);
};

// ---------------------------------------------------------------------------
// Grid utilities
// ---------------------------------------------------------------------------

/**
 * Build a grid of Cartographic positions centred on a fixed point (London).
 * Used for terrain-height sampling demos.
 * @param {number} rectangleHalfSize - Half-size in radians
 * @returns {Cesium.Cartographic[]}
 */
export function createGrid(rectangleHalfSize) {
    const gridWidth  = 41;
    const gridHeight = 41;
    const everestLatitude  = Cesium.Math.toRadians(51.51786);
    const everestLongitude = Cesium.Math.toRadians(-0.102216);
    const e = new Cesium.Rectangle(
        everestLongitude - rectangleHalfSize,
        everestLatitude  - rectangleHalfSize,
        everestLongitude + rectangleHalfSize,
        everestLatitude  + rectangleHalfSize
    );
    const positions = [];
    for (let y = 0; y < gridHeight; ++y) {
        for (let x = 0; x < gridWidth; ++x) {
            positions.push(new Cesium.Cartographic(
                Cesium.Math.lerp(e.west,  e.east,  x / (gridWidth  - 1)),
                Cesium.Math.lerp(e.south, e.north, y / (gridHeight - 1))
            ));
        }
    }
    return positions;
}

// ---------------------------------------------------------------------------
// Bounding-box parsing
// ---------------------------------------------------------------------------

/**
 * Parse a bounding box from various formats into an array or object of radians.
 *
 * Accepted input formats:
 *  - CSV string:  "west,south,east,north"  (degrees or radians auto-detected)
 *  - JSON string: "[w,s,e,n]" or "{west,south,east,north}"
 *  - Array:       [w, s, e, n]             (degrees or radians auto-detected)
 *  - Object:      {west, south, east, north}
 *
 * @param {string|Array|Object} bbox
 * @returns {number[]|Object|null}
 */
export const parseBoundingBox = (bbox) => {
    const toRadiansIfNeeded = (values) => {
        const maxAbs = Math.max(...values.map(Math.abs));
        if (maxAbs > Math.PI) {
            console.log('   Detected degrees, converting to radians');
            return values.map(deg => Cesium.Math.toRadians(deg));
        }
        return values;
    };

    if (typeof bbox === 'string') {
        const trimmed = bbox.trim();

        // JSON: "[…]" or "{…}"
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
            try {
                return JSON.parse(bbox);
            } catch (e) {
                console.error('Invalid JSON boundingBox:', e);
                return null;
            }
        }

        // CSV: "w,s,e,n"
        if (trimmed.includes(',')) {
            const values = trimmed.split(',').map(parseFloat);
            if (values.length !== 4 || values.some(isNaN)) {
                console.error('Invalid CSV boundingBox format');
                return null;
            }
            return toRadiansIfNeeded(values);
        }
    }

    if (Array.isArray(bbox)) return toRadiansIfNeeded(bbox);

    // Already an object – return as-is
    return bbox ?? null;
};
